import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import GlobalIntelligenceChyron from "@/components/GlobalIntelligenceChyron";

/**
 * CareLogix™ ORACLE — AdminOne Global Intelligence Chyron™ Manager (Phase 18)
 *
 * AdminOne control surface for the public broadcast chyron:
 *  - Settings: enable/disable, speed, pause-on-hover, severity/source filters
 *  - Items: list, add, archive (CRUD)
 *  - Live preview using the same <GlobalIntelligenceChyron /> component
 *
 * Strictly additive — independent route under /adminone/chyron.
 */

const SEVERITIES = [
  { key: "info",    label: "Info",    color: "#60b8e8" },
  { key: "ok",      label: "OK",      color: "#7ac860" },
  { key: "warn",    label: "Warning", color: "#f5c842" },
  { key: "high",    label: "High",    color: "#e87040" },
  { key: "extreme", label: "Extreme", color: "#c84a3a" },
];

const VISIBILITIES = [
  { key: "public",       label: "Public ORACLE Home" },
  { key: "adminone",     label: "AdminOne (Internal)" },
  { key: "paid_members", label: "Paid Members" },
  { key: "all",          label: "All Surfaces" },
];

const SPEEDS = ["slow", "normal", "fast"];

export default function AdminChyronManager() {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [feed, setFeed] = useState(null);

  // Form for new item
  const [newText, setNewText] = useState("");
  const [newSeverity, setNewSeverity] = useState("ok");
  const [newSource, setNewSource] = useState("adminone");
  const [newVisibility, setNewVisibility] = useState("public");
  const [newTargetUrl, setNewTargetUrl] = useState("");
  const [newTTL, setNewTTL] = useState(3600);

  const loadAll = useCallback(async () => {
    setErrMsg("");
    try {
      const [{ data: s }, { data: it }, { data: f }] = await Promise.all([
        adminApi.chyronSettingsGet(),
        adminApi.chyronItems("active", 100),
        adminApi.chyronFeed("adminone", 30),
      ]);
      setSettings(s?.settings || {});
      setItems(it?.items || []);
      setFeed(f || null);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Failed to load chyron data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const patchSetting = useCallback(async (patch) => {
    setBusy(true); setErrMsg("");
    try {
      const { data } = await adminApi.chyronSettingsPatch(patch);
      setSettings(data?.settings || {});
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Failed to save setting");
    } finally {
      setBusy(false);
    }
  }, []);

  const toggleSeverityFilter = useCallback((sev) => {
    const cur = new Set(settings?.severity_filter || []);
    if (cur.has(sev)) cur.delete(sev); else cur.add(sev);
    patchSetting({ severity_filter: Array.from(cur) });
  }, [settings, patchSetting]);

  const createItem = useCallback(async () => {
    setErrMsg("");
    if (!newText.trim()) {
      setErrMsg("Please enter text for the chyron item.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.chyronItemCreate({
        text: newText.trim(),
        severity: newSeverity,
        source: newSource || "adminone",
        visibility: newVisibility,
        target_url: newTargetUrl.trim() || null,
        ttl_seconds: Math.max(60, parseInt(newTTL, 10) || 3600),
      });
      setNewText("");
      setNewTargetUrl("");
      setSavedAt(new Date().toLocaleTimeString());
      await loadAll();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Failed to create item");
    } finally {
      setBusy(false);
    }
  }, [newText, newSeverity, newSource, newVisibility, newTargetUrl, newTTL, loadAll]);

  const archiveItem = useCallback(async (id) => {
    setBusy(true); setErrMsg("");
    try {
      await adminApi.chyronItemArchive(id);
      await loadAll();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Failed to archive item");
    } finally {
      setBusy(false);
    }
  }, [loadAll]);

  const sevFilter = useMemo(() => new Set(settings?.severity_filter || []), [settings]);

  if (!settings) {
    return (
      <div className="admin-page" data-testid="admin-chyron-manager-loading">
        <h2 className="admin-page__title">Global Intelligence Chyron™</h2>
        <div className="oracle-empty" style={{ padding: 14 }}>Loading chyron controls…</div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-chyron-manager">
      <div className="admin-page__head">
        <h2 className="admin-page__title">Global Intelligence Chyron™</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`admin-badge ${settings.enabled ? "is-on" : "is-off"}`} data-testid="admin-chyron-enabled-badge">
            {settings.enabled ? "BROADCAST LIVE" : "BROADCAST OFF"}
          </span>
          <span className="admin-badge is-on">COMPLIANCE GUARD</span>
          {savedAt && <span className="admin-saved">Saved {savedAt}</span>}
        </div>
      </div>

      {errMsg && (
        <div className="oracle-error" data-testid="admin-chyron-error">{errMsg}</div>
      )}

      {/* Live preview */}
      <section className="admin-section" data-testid="admin-chyron-preview">
        <h3 className="admin-section__title">Live Preview</h3>
        <div style={{
          border: "1px dashed var(--clx-border)",
          borderRadius: 12,
          padding: 14,
          background: "rgba(13,26,15,.45)",
          overflow: "hidden",
        }}>
          <div style={{ color: "var(--clx-muted)", textAlign: "center", fontFamily: "'Space Mono',monospace", fontSize: 11, letterSpacing: "0.18em" }}>
            ↓ Chyron renders fixed at the bottom of public ORACLE home (preview below)
          </div>
          {/* Render the same component, but contained inside this section */}
          <div style={{ position: "relative", marginTop: 12 }}>
            <GlobalIntelligenceChyron variant="public" className="gic--preview" />
          </div>
        </div>
        <p className="admin-foot-note" style={{ marginTop: 8 }}>
          The chyron is z-ordered below the LOGIX-2™ floating dock and respects <code>prefers-reduced-motion</code>.
        </p>
      </section>

      {/* Settings panel */}
      <section className="admin-section" data-testid="admin-chyron-settings">
        <h3 className="admin-section__title">Broadcast Settings</h3>
        <div className="admin-settings-grid">
          <label className="admin-toggle">
            <span>Chyron Enabled</span>
            <input
              type="checkbox"
              checked={!!settings.enabled}
              onChange={(e) => patchSetting({ enabled: e.target.checked })}
              disabled={busy}
              data-testid="admin-chyron-toggle-enabled"
            />
          </label>
          <label className="admin-toggle">
            <span>Pause on Hover</span>
            <input
              type="checkbox"
              checked={!!settings.pause_on_hover}
              onChange={(e) => patchSetting({ pause_on_hover: e.target.checked })}
              disabled={busy}
              data-testid="admin-chyron-toggle-pause"
            />
          </label>
          <label className="admin-toggle">
            <span>Respect Reduced Motion</span>
            <input
              type="checkbox"
              checked={!!settings.reduced_motion_respect}
              onChange={(e) => patchSetting({ reduced_motion_respect: e.target.checked })}
              disabled={busy}
              data-testid="admin-chyron-toggle-rmotion"
            />
          </label>
          <label className="admin-toggle">
            <span>Show on Public ORACLE</span>
            <input
              type="checkbox"
              checked={!!settings.show_public}
              onChange={(e) => patchSetting({ show_public: e.target.checked })}
              disabled={busy}
              data-testid="admin-chyron-toggle-public"
            />
          </label>
          <label className="admin-toggle">
            <span>Show on AdminOne</span>
            <input
              type="checkbox"
              checked={!!settings.show_adminone}
              onChange={(e) => patchSetting({ show_adminone: e.target.checked })}
              disabled={busy}
              data-testid="admin-chyron-toggle-adminone"
            />
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Scroll Speed</span>
            <select
              value={settings.speed || "normal"}
              onChange={(e) => patchSetting({ speed: e.target.value })}
              disabled={busy}
              data-testid="admin-chyron-speed"
            >
              {SPEEDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Max Items</span>
            <input
              type="number"
              min={3}
              max={80}
              value={settings.max_items ?? 30}
              onChange={(e) => patchSetting({ max_items: parseInt(e.target.value || "30", 10) })}
              disabled={busy}
              data-testid="admin-chyron-max-items"
            />
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Source Filter (comma-sep, blank=all)</span>
            <input
              type="text"
              value={Array.isArray(settings.source_filter) ? settings.source_filter.join(",") : (settings.source_filter || "")}
              placeholder="adminone,logix2,signal_fabric"
              onBlur={(e) => {
                const raw = (e.target.value || "").trim();
                patchSetting({ source_filter: raw ? raw.split(",").map((x) => x.trim()).filter(Boolean) : null });
              }}
              data-testid="admin-chyron-source-filter"
            />
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontFamily: "'Space Mono',monospace", color: "var(--clx-gold)", fontSize: 10.5, letterSpacing: "0.2em", marginBottom: 6 }}>
            SEVERITY FILTER
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SEVERITIES.map((s) => {
              const on = sevFilter.has(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  className="mesh-pill"
                  style={{
                    borderColor: s.color,
                    background: on ? `${s.color}22` : "transparent",
                    color: on ? s.color : "var(--clx-muted)",
                    cursor: "pointer",
                    minHeight: 32,
                  }}
                  onClick={() => toggleSeverityFilter(s.key)}
                  data-testid={`admin-chyron-sev-${s.key}`}
                  aria-pressed={on}
                  disabled={busy}
                >
                  ◆ {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* New item form */}
      <section className="admin-section" data-testid="admin-chyron-new-item">
        <h3 className="admin-section__title">+ Add Broadcast Item</h3>
        <div className="admin-settings-grid">
          <label className="admin-toggle admin-toggle--input" style={{ gridColumn: "span 2" }}>
            <span>Text · max 240 chars · compliance-guarded</span>
            <input
              type="text"
              maxLength={240}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="e.g. NVDA · Earnings beat · AI infra demand surging"
              data-testid="admin-chyron-new-text"
            />
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Severity</span>
            <select value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)} data-testid="admin-chyron-new-severity">
              {SEVERITIES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Visibility</span>
            <select value={newVisibility} onChange={(e) => setNewVisibility(e.target.value)} data-testid="admin-chyron-new-visibility">
              {VISIBILITIES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Source Tag</span>
            <input
              type="text"
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              placeholder="adminone"
              data-testid="admin-chyron-new-source"
            />
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>Target URL (optional)</span>
            <input
              type="text"
              value={newTargetUrl}
              onChange={(e) => setNewTargetUrl(e.target.value)}
              placeholder="/adminone/signals or https://…"
              data-testid="admin-chyron-new-url"
            />
          </label>
          <label className="admin-toggle admin-toggle--input">
            <span>TTL (seconds)</span>
            <input
              type="number"
              min={60}
              max={604800}
              value={newTTL}
              onChange={(e) => setNewTTL(parseInt(e.target.value || "3600", 10))}
              data-testid="admin-chyron-new-ttl"
            />
          </label>
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button
            type="button"
            className="oracle-btn oracle-btn--gold"
            onClick={createItem}
            disabled={busy || !newText.trim()}
            data-testid="admin-chyron-create-btn"
          >
            {busy ? "Saving…" : "+ Add to Broadcast"}
          </button>
          <button
            type="button"
            className="oracle-btn oracle-btn--ghost"
            onClick={() => loadAll()}
            disabled={busy}
            data-testid="admin-chyron-refresh"
          >
            ↻ Refresh
          </button>
        </div>
        <p className="admin-foot-note" style={{ marginTop: 8 }}>
          Items containing guaranteed-profit, sure-win, risk-free, or medical-emergency phrasing will be rejected by the
          Compliance Guard with HTTP 400.
        </p>
      </section>

      {/* Active items table */}
      <section className="admin-section" data-testid="admin-chyron-items">
        <h3 className="admin-section__title">Active Items · {items.length}</h3>
        {items.length === 0 ? (
          <div className="oracle-empty" style={{ padding: 14 }}>No active manual items yet. Live signals still appear via the Signal Fabric.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="admin-table" data-testid="admin-chyron-table">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Severity</th>
                  <th>Text</th>
                  <th style={{ width: 120 }}>Source</th>
                  <th style={{ width: 140 }}>Visibility</th>
                  <th style={{ width: 110 }}>Expires</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const sev = SEVERITIES.find((s) => s.key === it.severity) || SEVERITIES[1];
                  return (
                    <tr key={it.id} data-testid={`admin-chyron-row-${it.id}`}>
                      <td>
                        <span className="mesh-pill" style={{ borderColor: sev.color, color: sev.color, background: `${sev.color}11` }}>
                          ◆ {sev.label}
                        </span>
                      </td>
                      <td className="admin-table__truncate" title={it.text}>{it.text}</td>
                      <td><code>{it.source}</code></td>
                      <td><code>{it.visibility}</code></td>
                      <td style={{ fontFamily: "'Space Mono',monospace", fontSize: 11 }}>
                        {(it.expires_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="oracle-btn oracle-btn--ghost oracle-btn--sm mesh-btn--danger"
                          onClick={() => archiveItem(it.id)}
                          disabled={busy}
                          data-testid={`admin-chyron-archive-${it.id}`}
                        >
                          Archive
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Diagnostics block */}
      {feed && (
        <section className="admin-section" data-testid="admin-chyron-diagnostics">
          <h3 className="admin-section__title">Aggregator Diagnostics</h3>
          <div className="admin-metric-grid">
            <div className="admin-metric"><div className="admin-metric__label">Items merged</div><div className="admin-metric__value">{feed?.items?.length ?? 0}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Speed</div><div className="admin-metric__value" style={{ fontSize: 18 }}>{(settings.speed || "normal").toUpperCase()}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Schema</div><div className="admin-metric__value" style={{ fontSize: 18 }}>{feed?.schema_version || "v1.0"}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Powered by</div><div className="admin-metric__value" style={{ fontSize: 14 }}>{feed?.powered_by || "GIC™"}</div></div>
          </div>
        </section>
      )}
    </div>
  );
}
